import _ from 'lodash';
import classNames from 'classnames';
import {
	Button,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Radio,
	RadioGroup,
	TextField,
	Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useState } from 'react';
import HotelNameAutocomplete from './AdvancedFilters/HotelNameAutocomplete';
import BookingsDateFilter from './BookingsDateFilter';
import SuperAgentBranchesAutocomplete from './AdvancedFilters/SuperAgentBranchesAutocomplete';
import CompanyNameAutocomplete from './AdvancedFilters/CompanyNameAutocomplete';
import { useTranslation } from 'react-i18next';
import AgentNameAutocomplete from './AdvancedFilters/AgentNameAutocomplete';
import Ajax from '../../../utils/Ajax';
import { FIELD_PADDING } from './SettlePayment/const';

const NewBookingsFiltersComponent = ({
	open,
	container,
	profile,
	filters,
	setFilters,
	initFilters,
	setPagination,
	initPagination,
	onClose,
	search,
}) => {
	const { t } = useTranslation();

	const [destination, setDestination] = useState(filters.destination);
	const [hotelName, setHotelName] = useState(filters.hotel_name);
	const [hotelId, setHotelId] = useState(filters.hotel_id);
	const [reservationId, setReservationId] = useState(filters.reservation_id);
	const [travelerFirstName, setTravelerFirstName] = useState(filters.traveler_first_name);
	const [travelerLastName, setTravelerLastName] = useState(filters.traveler_last_name);

	// agent
	const [agentDropdown, setAgentDropdown] = useState();
	const [agentId, setAgentId] = useState(filters.agent_id);
	const [agentName, setAgentName] = useState(filters.agent_name);
	const [contractId, setContractId] = useState(filters.contract_id);
	const [companyName, setCompanyName] = useState(filters.contract_name);
	const [bookingFromBranch, setBookingFromBranch] = useState(filters.bookings_from_branch);

	const [travelAgents, setTravelAgents] = useState([]);

	const [dateFilters, setDateFilters] = useState({
		start_date: filters.start_date,
		end_date: filters.end_date,
		cancellation_deadline_start: filters.cancellation_deadline_start,
		cancellation_deadline_end: filters.cancellation_deadline_end,
	});

	const fetchAgents = async () => {
		setAgentSelection();
		setTravelAgents([]);
		if (_.isEmpty(bookingFromBranch)) {
			return;
		}

		try {
			const users = await Ajax.getAgents({ branch_id: bookingFromBranch });
			setTravelAgents(users || []);
		} catch (err) {
			console.error(err);
			setTravelAgents([]);
		}
	};

	useEffect(() => {
		fetchAgents();
	}, [bookingFromBranch]);

	const fetchAgentsByContract = async () => {
		setAgentSelection();
		setTravelAgents([]);
		if (!(isAgent || isSuperAgent) || !contractId) {
			return;
		}

		try {
			const users = await Ajax.getAgents({ contract_id: contractId });
			setTravelAgents(users || []);
		} catch (err) {
			console.error(err);
			setTravelAgents([]);
		}
	};

	useEffect(() => {
		fetchAgentsByContract();
	}, [contractId]);

	useEffect(() => {
		if (_.isEmpty(filters.bookings_from_branch)) {
			// default to My Bookings
			setAgentDropdown(t('AdvancedFilters.MyBookings'));
		} else {
			// set to By Branch
			setBookingFromBranch(filters.bookings_from_branch);
			setAgentDropdown(t('AdvancedFilters.ByBranch'));
		}
	}, [filters.bookings_from_branch]);

	useEffect(() => {
		if (_.isEmpty(filters.contract_id)) {
			setAgentDropdown(t('AdvancedFilters.MyBookings'));
		}
	}, [filters.contract_id]);

	useEffect(() => {
		setDestination(filters.destination);
		setHotelName(filters.hotel_name);
		setHotelId(filters.hotel_id);
		setReservationId(filters.reservation_id);
		setTravelerFirstName(filters.traveler_first_name);
		setTravelerLastName(filters.traveler_last_name);
		setContractId(filters.contract_id);
		setCompanyName(filters.company_name);
		setBookingFromBranch(filters.bookings_from_branch);
		setAgentId(filters.agent_id);
		setAgentName(filters.agent_name);

		setDateFilters({
			start_date: filters.start_date,
			end_date: filters.end_date,
			cancellation_deadline_start: filters.cancellation_deadline_start,
			cancellation_deadline_end: filters.cancellation_deadline_end,
		});
	}, [filters]);

	// Only keep active contracts
	const contractList = useMemo(() => {
		return (profile?.agency_contracts || []).filter((c) => c?.active !== false);
	}, [profile]);

	const isAgent = profile.agent && !profile.super_agent;
	const isSuperAgent = profile.super_agent;
	const hasAgentBranch = isAgent && profile.agency_branch;
	const hasBranches = Array.isArray(profile.company_branches) && profile.company_branches.length > 0;

	const setAgentSelection = (agentOption) => {
		if (!agentOption) {
			setAgentId(initFilters.agent_id);
			setAgentName(initFilters.agent_name);
			return;
		}
		setAgentId(agentOption._id);
		setAgentName(agentOption.full_name || agentOption.name || agentOption.email || '');
	};

	const onChangeAgentDropdown = (e, val) => {
		switch (val) {
			case t('AdvancedFilters.MyBookings'):
				setTravelAgents([]);
				setAgentId(null);
				setAgentName('');
				setBookingFromBranch(null);
				break;

			case t('AdvancedFilters.ByBranch'):
				if (isAgent && hasAgentBranch) {
					setBookingFromBranch(profile.agency_branch?._id);
				}
				if (isSuperAgent && hasBranches) {
					setBookingFromBranch(filters.bookings_from_branch);
				}
				break;
			case t('AdvancedFilters.ByCompany'):
				setTravelAgents([]);
				setAgentId(null);
				setAgentName('');
				setBookingFromBranch(null);
				break;
			default:
				break;
		}

		setAgentDropdown(val);
	};

	const setHotelSelection = (hotelOption) => {
		setHotelId(hotelOption.hotel_id);
		setHotelName(hotelOption.hotel_name);
	};

	const applyFilters = () => {
		setPagination(initPagination);
		setFilters({
			...filters,
			destination,
			hotel_id: hotelId,
			hotel_name: hotelName,
			reservation_id: reservationId,
			traveler_first_name: travelerFirstName,
			traveler_last_name: travelerLastName,
			agent_name: agentName,
			agent_id: agentId,
			contract_id: contractId,
			company_name: companyName,
			bookings_from_branch: bookingFromBranch,
			...dateFilters,
			search,
		});
		onClose();
	};

	const isAgentOrSuperAgent = isAgent || isSuperAgent;
	const hasTravelAgents = travelAgents.length > 0;

	const showBranchAgentName =
		agentDropdown === t('AdvancedFilters.ByBranch') && hasTravelAgents && isAgentOrSuperAgent;

	const showCompanyAgentName =
		agentDropdown === t('AdvancedFilters.ByCompany') && isAgentOrSuperAgent && !!contractId && hasTravelAgents;

	const showAgentName = showBranchAgentName || showCompanyAgentName;

	// for user with only self contract - don't show company radio button
	const showCompany = isAgentOrSuperAgent && contractList.length > 1;

	return (
		<Slide direction="down" in={open} mountOnEnter unmountOnExit container={container}>
			<div className="new-bookings-filters-slide">
				<div className="new-bookings-filters-slide__content-wrapper">
					<div
						className={classNames('new-bookings-filters-slide__content', {
							'justify-center': !isAgent && !isSuperAgent,
						})}
					>
						{(isAgent || isSuperAgent) && (
							<div className="new-bookings-filters-slide__content__border-end">
								<div className="new-bookings-filters-component__filter">
									<label>{t('AdvancedFilters.ShowBookingsFrom')}</label>
									<>
										<RadioGroup value={agentDropdown} onChange={onChangeAgentDropdown}>
											<FormControlLabel
												value={t('AdvancedFilters.MyBookings')}
												control={<Radio />}
												label={t('AdvancedFilters.MyBookings')}
											/>

											{isAgent && hasAgentBranch && (
												<FormControlLabel
													value={t('AdvancedFilters.ByBranch')}
													control={<Radio />}
													label={profile.agency_branch?.name}
												/>
											)}

											{isSuperAgent && hasBranches && (
												<FormControlLabel
													value={t('AdvancedFilters.ByBranch')}
													control={<Radio />}
													label={t('AdvancedFilters.ByBranch')}
												/>
											)}

											{showCompany && (
												<FormControlLabel
													value={t('AdvancedFilters.ByCompany')}
													control={<Radio />}
													label={t('AdvancedFilters.ByCompany')}
												/>
											)}
										</RadioGroup>

										{isSuperAgent && agentDropdown === t('AdvancedFilters.ByBranch') && (
											<div className="new-bookings-filters-component__dropdown">
												<SuperAgentBranchesAutocomplete
													profile={profile}
													bookingFromBranch={bookingFromBranch}
													onChange={setBookingFromBranch}
												/>
											</div>
										)}

										{showCompany && agentDropdown === t('AdvancedFilters.ByCompany') && (
											<div className="new-bookings-filters-component__dropdown">
												<CompanyNameAutocomplete
													profile={profile}
													id="company-name-input"
													value={contractId}
													onChange={setContractId}
												/>
											</div>
										)}
									</>
								</div>
							</div>
						)}

						<div>
							<div className="flex flex-start">
								<div className="new-bookings-filters-component__filter">
									{/* Destination */}
									<label htmlFor="destination-input">{t('AdvancedFilters.Destination')}</label>
									<TextField
										id="destination-input"
										className="field"
										placeholder={t('AdvancedFilters.FindByDestinationPlaceholder')}
										variant="outlined"
										value={destination}
										size="small"
										onChange={(e) => setDestination(e.target.value)}
										sx={FIELD_PADDING}
										InputProps={{
											endAdornment: destination && (
												<InputAdornment position="end">
													<IconButton
														onClick={() => setDestination(initFilters.destination)}
														disableRipple
													>
														<CloseIcon />
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</div>
								<div className="new-bookings-filters-component__filter">
									{/* Hotel Name */}
									<label htmlFor="hotel-input">{t('AdvancedFilters.HotelName')}</label>
									<HotelNameAutocomplete
										id="hotel-input"
										className="field"
										value={hotelName}
										onChange={(option) => setHotelSelection(option)}
									/>
								</div>
							</div>

							{showAgentName && (
								<>
									<div className="new-bookings-filters-component__space" />
									<div className="flex flex-start">
										{/* Agent Name */}
										<div className="new-bookings-filters-component__filter">
											<label htmlFor="agent-name-input">{t('AdvancedFilters.AgentName')}</label>

											<AgentNameAutocomplete
												travelAgents={travelAgents}
												selectedAgentId={agentId ?? ''}
												onChange={setAgentSelection}
												placeholder={t('FindByAgentName')}
											/>
										</div>
									</div>
								</>
							)}
						</div>

						<div>
							<BookingsDateFilter
								filters={dateFilters}
								initFilters={initFilters}
								setFilters={setDateFilters}
							/>
						</div>
					</div>
				</div>
				{/* Actions */}
				<div className="new-bookings-filters-slide__actions flex-center">
					<Button
						className="new-bookings-filters-wrap-component__button new-bookings-filters-wrap-component__cancel"
						variant="outlined"
						onClick={onClose}
						disableRipple
					>
						{t('AdvancedFilters.Cancel')}
					</Button>
					<Button
						className="new-bookings-filters-wrap-component__button"
						variant="contained"
						onClick={applyFilters}
						disableRipple
					>
						{t('AdvancedFilters.Apply')}
					</Button>
				</div>
			</div>
		</Slide>
	);
};

export default NewBookingsFiltersComponent;
